import { asset } from '../assets';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { ProductGallery } from '../components/ProductGallery';
import { NotesField } from '../components/NotesField';
import { CustomSizeField } from '../components/CustomSizeField';
import { QuoteActions } from '../components/QuoteActions';
import { usePageSetup } from '../hooks/usePageSetup';

const title = "Self Carbon Notebooks - INGAZ Print";

function SelfCarbonNotebooks() {
  usePageSetup({ title });

  return (
    <>
      <SiteHeader />
        <main>
          <section className="product-details-section">
            <div className="product-container">
              <ProductGallery alt="Self Carbon Notebooks" images={[asset('assets/self_carbon_notebook_1.png'), asset('assets/self_carbon_notebook_2.png'), asset('assets/self_carbon_notebook_3.png'), asset('assets/image17.webp')]} />
              <div className="product-info">
                <h1 className="product-title" data-en="Self Carbon Notebooks" data-ar="دفاتر الكربون الذاتي">Self Carbon Notebooks
                </h1>
                <p className="product-description" data-en="Carbonless copy notebooks with duplicate pages for instant record keeping and receipts." data-ar="دفاتر نسخ كربونية بصفحات مكررة لحفظ السجلات والإيصالات الفورية.">Carbonless copy notebooks
                  with duplicate pages for instant record keeping and receipts.</p>
                <div className="key-features">
                  <div className="feature-item">
                    <div className="feature-icon-small"><img src={asset('assets/time.svg')} alt="" /></div>
                    <div>
                      <div className="feature-name" data-en="Year-Round" data-ar="طوال العام">Year-Round</div>
                      <div className="feature-detail" data-en="12 months" data-ar="12 شهرًا">12 months</div>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon-small"><img src={asset('assets/star.svg')} alt="" /></div>
                    <div>
                      <div className="feature-name" data-en="Customizable" data-ar="قابل للتخصيص">Customizable</div>
                      <div className="feature-detail" data-en="Your images" data-ar="صورك">Your images</div>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon-small"><img src={asset('assets/truck.svg')} alt="" /></div>
                    <div>
                      <div className="feature-name" data-en="Fast Delivery" data-ar="تسليم سريع">Fast Delivery</div>
                      <div className="feature-detail" data-en="Quick turnaround" data-ar="إنجاز سريع">Quick turnaround</div>
                    </div>
                  </div>
                </div>
                <div className="product-benefits">
                  <h3 data-en="Why this product works" data-ar="لماذا يعمل هذا المنتج">Why this product works</h3>
                  <ul className="benefits-list">
                    <li data-en="Instant duplicate copies without carbon paper" data-ar="نسخ مكررة فورية دون ورق الكربون">
                      Instant duplicate copies without carbon paper</li>
                    <li data-en="Perfect for invoices, receipts, and forms" data-ar="مثالي للفواتير والإيصالات والنماذج">
                      Perfect for invoices, receipts, and forms</li>
                    <li data-en="Customizable with your company details" data-ar="قابل للتخصيص بتفاصيل شركتك">
                      Customizable with your company details</li>
                    <li data-en="Professional NCR paper quality" data-ar="جودة ورق NCR احترافية">
                      Professional NCR paper quality</li>
                  </ul>
                </div>
                {/* Common Use Cases */}
                <div className="use-cases">
                  <h3 data-en="COMMON USE CASES" data-ar="حالات الاستخدام الشائعة">COMMON USE CASES</h3>
                  <div className="use-cases-grid">
                    <div className="use-case-item">
                      <div className="use-case-icon"><img src={asset('assets/dollar.svg')} alt="" /></div>
                      <div className="use-case-name" data-en="Invoices" data-ar="الفواتير">Invoices</div>
                    </div>
                    <div className="use-case-item">
                      <div className="use-case-icon"><img src={asset('assets/bag.svg')} alt="" /></div>
                      <div className="use-case-name" data-en="Receipts" data-ar="الإيصالات">Receipts</div>
                    </div>
                    <div className="use-case-item">
                      <div className="use-case-icon"><img src={asset('assets/truck.svg')} alt="" /></div>
                      <div className="use-case-name" data-en="Delivery Notes" data-ar="مذكرات التسليم">Delivery Notes</div>
                    </div>
                    <div className="use-case-item">
                      <div className="use-case-icon"><img src={asset('assets/store.svg')} alt="" /></div>
                      <div className="use-case-name" data-en="Sales Forms" data-ar="نماذج المبيعات">Sales Forms</div>
                    </div>
                  </div>
                </div>
                <QuoteActions configureOnly productType={title.replace(' - INGAZ Print', '')} />
                <p className="help-text" data-en="Need help? Our design team is ready to assist you." data-ar="تحتاج مساعدة؟ فريق التصميم لدينا جاهز لمساعدتك.">Need help? Our design team is ready to assist you.
                </p>
              </div>
            </div>
          </section>
          <section className="configuration-section" id="configuration">
            <div className="config-container">
              <div className="config-main">
                <h2 data-en="Configure Your Self-Carbon Notebooks" data-ar="قم بتكوين دفاتر النسخ الكربوني الذاتي">Configure Your Self-Carbon Notebooks</h2>
                
                {/* Quantity */}
                <div className="config-group">
                  <label htmlFor="quantity" data-en="Quantity" data-ar="الكمية">Quantity</label>
                  <input type="number" id="quantity" placeholder="Enter quantity" min={1} defaultValue={10} data-placeholder-en="Enter quantity" data-placeholder-ar="أدخل الكمية" />
                </div>

                {/* Number of sets per book */}
                <div className="config-group">
                  <label htmlFor="setsPerBook" data-en="Number of sets per book" data-ar="عدد المجموعات لكل دفتر">Number of sets per book</label>
                  <p style={{fontSize: '13px', color: '#6c757d', marginBottom: '8px'}} data-en="(A 'set' = one full carbonless copy group, e.g. white + yellow + pink)" data-ar="(المجموعة = مجموعة نسخ خالية من الكربون كاملة، مثل أبيض + أصفر + وردي)">(A 'set' = one full carbonless copy group, e.g. white + yellow + pink)</p>
                  <input type="number" id="setsPerBook" placeholder="e.g. 50" min={1} defaultValue={50} data-placeholder-en="e.g. 50" data-placeholder-ar="مثل 50" className="custom-size-input" />
                </div>

                {/* Copies per set */}
                <div className="config-group">
                  <label data-en="Number of copies per set (NCR layers)" data-ar="عدد النسخ لكل مجموعة (طبقات NCR)">Number of copies per set (NCR layers)</label>
                  <div className="lamination-options">
                    <label className="radio-option">
                      <input type="radio" name="ncrLayers" defaultValue="2" defaultChecked onChange={(e) => document.getElementById('otherCopiesWrapper').style.display = 'none'} />
                      <span data-en="2 copies (e.g. white + yellow)" data-ar="نسختان (أبيض + أصفر)">2 copies (e.g. white + yellow)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="ncrLayers" defaultValue="3" onChange={(e) => document.getElementById('otherCopiesWrapper').style.display = 'none'} />
                      <span data-en="3 copies (e.g. white + yellow + pink)" data-ar="3 نسخ (أبيض + أصفر + وردي)">3 copies (e.g. white + yellow + pink)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="ncrLayers" defaultValue="4" onChange={(e) => document.getElementById('otherCopiesWrapper').style.display = 'none'} />
                      <span data-en="4 copies (e.g. white + yellow + pink + green)" data-ar="4 نسخ (أبيض + أصفر + وردي + أخضر)">4 copies (e.g. white + yellow + pink + green)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="ncrLayers" defaultValue="other" onChange={(e) => document.getElementById('otherCopiesWrapper').style.display = 'block'} />
                      <span data-en="Other" data-ar="آخر">Other</span>
                    </label>
                  </div>
                  <div id="otherCopiesWrapper" style={{display: 'none', marginTop: '10px'}}>
                    <input type="number" id="otherCopiesInput" className="custom-size-input" min={1} placeholder="Enter number of copies" data-placeholder-en="Enter number of copies" data-placeholder-ar="أدخل عدد النسخ" />
                  </div>
                </div>

                {/* Size */}
                <div className="config-group">
                  <label data-en="Size" data-ar="الحجم">Size</label>
                  <div className="size-grid">
                    <div className="size-option active" data-size="A4" data-dims="21x29.7 cm"><span>A4</span><br /><span>21x29.7 cm</span></div>
                    <div className="size-option" data-size="A5" data-dims="14.8x21 cm"><span>A5</span><br /><span>14.8x21 cm</span></div>
                    <div className="size-option" data-size="A6" data-dims="10.5x14.8 cm"><span>A6</span><br /><span>10.5x14.8 cm</span></div>
                    <div className="size-option" data-size="Other" data-dims="Custom"><span data-en="Other" data-ar="آخر">Other</span><br /><span data-en="Custom" data-ar="مخصص">Custom</span></div>
                  </div>
                  <CustomSizeField />
                </div>

                {/* Orientation */}
                <div className="config-group">
                  <label data-en="Orientation" data-ar="الاتجاه">Orientation</label>
                  <div className="lamination-options">
                    <label className="radio-option">
                      <input type="radio" name="orientation" defaultValue="portrait" defaultChecked />
                      <span data-en="Portrait" data-ar="طولي">Portrait</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="orientation" defaultValue="landscape" />
                      <span data-en="Landscape" data-ar="عرضي">Landscape</span>
                    </label>
                  </div>
                </div>

                {/* Printed Colors */}
                <div className="config-group">
                  <label data-en="Printed Colors" data-ar="الألوان المطبوعة">Printed Colors</label>
                  <div className="color-options">
                    <div className="color-option active" data-color="colored">
                      <img src={asset('assets/palette.svg')} alt="" className="color-icon" />
                      <div className="color-dots">
                        <span className="dot red" />
                        <span className="dot green" />
                        <span className="dot blue" />
                      </div>
                      <span data-en="Full Color" data-ar="ألوان كاملة">Full Color</span>
                    </div>
                    <div className="color-option" data-color="bw">
                      <div className="color-dots">
                        <span className="dot black" />
                        <span className="dot white" />
                      </div>
                      <span data-en="Black & White" data-ar="أبيض وأسود">Black &amp; White</span>
                    </div>
                  </div>
                </div>

                {/* Fixed Printed Sides */}
                <div className="config-group" style={{ display: 'none' }}>
                  <label data-en="Printed Sides" data-ar="الجوانب المطبوعة">Printed Sides</label>
                  <input type="text" defaultValue="Single side" />
                </div>
                
                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '25px', fontSize: '14px', color: '#555' }}>
                  <strong data-en="Product Defaults:" data-ar="الإعدادات الافتراضية للمنتج:">Product Defaults:</strong><br/>
                  - <span data-en="Printed on single side" data-ar="مطبوع على جانب واحد">Printed on single side</span>
                </div>

                {/* Numbering */}
                <div className="config-group">
                  <label data-en="Numbering (sequential numbers on each set)" data-ar="الترقيم (أرقام متسلسلة على كل مجموعة)">Numbering (sequential numbers on each set)</label>
                  <div className="lamination-options">
                    <label className="radio-option">
                      <input type="radio" name="numbering" defaultValue="yes" onChange={(e) => document.getElementById('numberingField').style.display = 'block'} />
                      <span data-en="Yes" data-ar="نعم">Yes</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="numbering" defaultValue="no" defaultChecked onChange={(e) => document.getElementById('numberingField').style.display = 'none'} />
                      <span data-en="No" data-ar="لا">No</span>
                    </label>
                  </div>
                  <div id="numberingField" style={{display: 'none', marginTop: '10px'}}>
                    <input type="number" className="custom-size-input" placeholder="Starting number (e.g. 1)" data-placeholder-en="Starting number (e.g. 1)" data-placeholder-ar="رقم البداية (مثل 1)" />
                  </div>
                </div>

                {/* Binding */}
                <div className="config-group">
                  <label data-en="Binding" data-ar="التجليد">Binding</label>
                  <div className="lamination-options">
                    <label className="radio-option">
                      <input type="radio" name="binding" defaultValue="glued-top" defaultChecked />
                      <span data-en="Glued on top edge (tear-off book)" data-ar="ملصق من الحافة العلوية (دفتر للقطع)">Glued on top edge (tear-off book)</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="binding" defaultValue="glued-side" />
                      <span data-en="Glued on side edge" data-ar="ملصق من الحافة الجانبية">Glued on side edge</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="binding" defaultValue="stapled" />
                      <span data-en="Stapled" data-ar="مدبس">Stapled</span>
                    </label>
                  </div>
                </div>
                
                {/* Perforation */}
                <div className="config-group">
                  <label data-en="Perforation (for easy tear-off)" data-ar="التخريم (لسهولة القطع)">Perforation (for easy tear-off)</label>
                  <div className="lamination-options">
                    <label className="radio-option">
                      <input type="radio" name="perforation" defaultValue="yes" defaultChecked />
                      <span data-en="Yes" data-ar="نعم">Yes</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="perforation" defaultValue="no" />
                      <span data-en="No" data-ar="لا">No</span>
                    </label>
                  </div>
                </div>

                {/* Packaging */}
                <div className="config-group">
                  <label htmlFor="packaging" data-en="Packaging" data-ar="التعبئة والتغليف">Packaging</label>
                  <textarea id="packaging" placeholder="Describe your packaging requirements" rows={3} data-placeholder-en="Describe your packaging requirements" data-placeholder-ar="اوصف متطلبات التعبئة والتغليف" defaultValue={""} />
                </div>
                
                {/* Upload Design Files */}
                <div className="config-group">
                  <label data-en="Upload artwork" data-ar="رفع العمل الفني">Upload artwork</label>
                  <div className="upload-area" id="uploadArea">
                    <div className="upload-content">
                      <div className="upload-icon"><img src={asset('assets/upload.svg')} alt="" /></div>
                      <p data-en="Drop your files here or browse" data-ar="قم بإسقاط ملفاتك هنا أو تصفح">Drop your files here
                        or browse</p>
                      <p className="upload-hint" data-en="Supports: PDF, AI, EPS, PNG, JPG, PSD (Max 100MB each)" data-ar="يدعم: PDF, AI, EPS, PNG, JPG, PSD (الحد الأقصى 100 ميجابايت لكل ملف)">Supports: PDF, AI, EPS,
                        PNG, JPG, PSD (Max 100MB each)</p>
                    </div>
                    <input type="file" id="fileInput" multiple accept=".pdf,.ai,.eps,.png,.jpg,.jpeg,.psd" style={{display: 'none'}} />
                  </div>
                </div>

                <NotesField />
              </div>
              {/* Request Summary Sidebar */}
              <div className="request-summary">
                <h3 data-en="Request Summary" data-ar="ملخص الطلب">Request Summary</h3>
                <div className="summary-details">
                  <div className="summary-item">
                    <span className="summary-label" data-en="Size:" data-ar="الحجم:">Size:</span>
                    <span className="summary-value" id="summarySize">A4</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label" data-en="Color:" data-ar="اللون:">Color:</span>
                    <span className="summary-value" id="summaryColor">Full Color</span>
                  </div>
                </div>
                <div className="delivery-estimate">
                  <strong data-en="Estimated Delivery" data-ar="التسليم المتوقع">Estimated Delivery</strong>
                  <div className="delivery-time" data-en="1-2 business days" data-ar="1-2 أيام عمل">1-2 business days</div>
                </div>
                <QuoteActions productType={title.replace(' - INGAZ Print', '')} />
                <button className="btn3"><img src={asset('assets/phone.svg')} alt="" /> <span data-en="Contact Support" data-ar="اتصل بالدعم">Contact Support</span></button>
                <div className="guarantees">
                  <div className="guarantee-item" data-en="✓ Free design review" data-ar="✓ مراجعة تصميم مجانية">✓ Free design
                    review</div>
                  <div className="guarantee-item" data-en="✓ Quality guarantee" data-ar="✓ ضمان الجودة">✓ Quality guarantee</div>
                  <div className="guarantee-item" data-en="✓ Fast turnaround" data-ar="✓ تسليم سريع">✓ Fast turnaround</div>
                </div>
              </div>
            </div>
          </section>
          {/* Why Choose INGAZ Section */}
          <section className="why-choose-section">
            <div className="why-choose-container">
              <h2 data-en="Why Choose INGAZ?" data-ar="لماذا تختار INGAZ؟">Why Choose INGAZ?</h2>
              <p className="why-choose-subtitle" data-en="Your trusted partner for professional printing solutions" data-ar="شريكك الموثوق لحلول الطباعة الاحترافية">Your trusted partner for professional printing solutions</p>
              <div className="why-choose-grid">
                <div className="why-choose-card">
                  <div className="why-icon"><img src={asset('assets/quality.svg')} alt="" /></div>
                  <h3 data-en="High Quality" data-ar="جودة عالية">High Quality</h3>
                  <p data-en="Premium materials and cutting-edge printing technology" data-ar="مواد عالية الجودة وتقنيات طباعة متطورة">Premium materials and cutting-edge printing technology
                  </p>
                </div>
                <div className="why-choose-card">
                  <div className="why-icon"><img src={asset('assets/power.svg')} alt="" /></div>
                  <h3 data-en="Innovative Solutions" data-ar="حلول مبتكرة">Innovative Solutions</h3>
                  <p data-en="Modern printing techniques for exceptional results" data-ar="تقنيات طباعة حديثة لنتائج استثنائية">Modern printing techniques for exceptional results</p>
                </div>
                <div className="why-choose-card">
                  <div className="why-icon"><img src={asset('assets/time.svg')} alt="" /></div>
                  <h3 data-en="Fast Turnaround" data-ar="تسليم سريع">Fast Turnaround</h3>
                  <p data-en="Express delivery options available for urgent orders" data-ar="خيارات تسليم سريع متاحة للطلبات العاجلة">Express delivery options available for urgent orders</p>
                </div>
                <div className="why-choose-card">
                  <div className="why-icon"><img src={asset('assets/dollar.svg')} alt="" /></div>
                  <h3 data-en="Value for Money" data-ar="قيمة مقابل المال">Value for Money</h3>
                  <p data-en="Competitive pricing without compromising on quality" data-ar="أسعار تنافسية دون المساس بالجودة">
                    Competitive pricing without compromising on quality</p>
                </div>
              </div>
            </div>
          </section>
          {/* Related Products Section */}
          <section className="related-products-section">
            <div className="related-products-container">
              <h2 data-en="Related Products" data-ar="منتجات ذات صلة">Related Products</h2>
              <p className="related-subtitle" data-en="Explore our full range of printing services" data-ar="استكشف مجموعة كاملة من خدمات الطباعة">Explore our full range of printing services</p>
              <div className="related-products-grid">
                <div className="related-product-card">
                  <div className="related-product-image">
                    <img src={asset('assets/image1.webp')} alt="Business Cards" />
                  </div>
                  <h3 data-en="Business Cards" data-ar="بطاقات العمل">Business Cards</h3>
                  <p data-en="Professional business cards" data-ar="بطاقات عمل احترافية">Professional business cards</p>
                  <a href="/business-cards" className="btn2"><span data-en="Request quote" data-ar="طلب عرض أسعار">Request quote</span> <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" /></a>
                </div>
                <div className="related-product-card">
                  <div className="related-product-image">
                    <img src={asset('assets/image5.webp')} alt="Brochures" />
                  </div>
                  <h3 data-en="Brochures" data-ar="الكتيبات">Brochures</h3>
                  <p data-en="Multi-page marketing material" data-ar="مواد تسويقية متعددة الصفحات">Multi-page marketing
                    material</p>
                  <a href="/brochures" className="btn2"><span data-en="Request quote" data-ar="طلب عرض أسعار">Request quote</span> <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" /></a>
                </div>
                <div className="related-product-card">
                  <div className="related-product-image">
                    <img src={asset('assets/image3.webp')} alt="Leaflets" />
                  </div>
                  <h3 data-en="Leaflets" data-ar="المنشورات المطوية">Leaflets</h3>
                  <p data-en="Folded marketing materials" data-ar="مواد تسويقية مطوية">Folded marketing materials</p>
                  <a href="/leaflets" className="btn2"><span data-en="Request quote" data-ar="طلب عرض أسعار">Request quote</span> <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" /></a>
                </div>
                <div className="related-product-card">
                  <div className="related-product-image">
                    <img src={asset('assets/image7.webp')} alt="Catalogs" />
                  </div>
                  <h3 data-en="Catalogs" data-ar="الكتالوجات">Catalogs</h3>
                  <p data-en="Product catalogs & lookbooks" data-ar="كتالوجات المنتجات والكتيبات">Product catalogs &amp; lookbooks
                  </p>
                  <a href="/catalogues" className="btn2"><span data-en="Request quote" data-ar="طلب عرض أسعار">Request quote</span> <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" /></a>
                </div>
                <div className="related-product-card">
                  <div className="related-product-image">
                    <img src={asset('assets/image8.webp')} alt="Menus" />
                  </div>
                  <h3 data-en="Menus" data-ar="القوائم">Menus</h3>
                  <p data-en="Restaurant quality printing" data-ar="طباعة بجودة المطاعم">Restaurant quality printing</p>
                  <a href="/menus" className="btn2"><span data-en="Request quote" data-ar="طلب عرض أسعار">Request quote</span> <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" /></a>
                </div>
              </div>
            </div>
          </section>
          {/* Footer */}
          <SiteFooter />
        </main>
    </>
  );
}

export default SelfCarbonNotebooks;
